import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import dataModel from '../../models/DataModel';
import PageStateDAO from '../../dao/PageStateDAO';
import LocationsCollection from '../../dao/collections/LocationsCollection';
import ProjectTypesCollection from '../../dao/collections/ProjectTypesCollection';
import { PageNames } from '../../constants/ReduxConstants';
import AnalyticsLogger from '../../helpers/analytics/analyticsLogger';
import MapsHelpers from '../../helpers/MapsHelpers/MapsHelpers';

import MapView from '../../components/MapView';
import MobileHeader from '../../components/MobileHeader';
import MobileLocationMapMarker from '../../components/MobileLocationMapMarker';
import MapLocationPopup from '../../components/MapLocationPopup';
import LocationMapCluster from '../../components/LocationMapCluster';
import LoaderPopup from '../../components/LoaderPopup';

import '../MobileWrapper.scss';
import './MapsWrapper.scss';

const locationDescriptor = {
  location: {
    additionalFields: ['not_started_projects_count', 'in_progress_projects_count', 'completed_projects_count', 'has_alerts', 'has_unread_alerts', 'has_delays'],
  },
};

export class MapsWrapper extends React.Component {

  constructor(props) {
    super(props);
    this.mapOptions = {
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: false,
    };

    this.noBackButton = !location.href.includes('mapMyDay');

    this.locationsCollection = LocationsCollection.build(null, null, null, dataModel.usersDAO.currentUser.id, localStorage.current_company_id, 'MAPSWRAPPER', {}, locationDescriptor, null);
    const collectionFilter = this.locationsCollection.getFilter();
    if (this.isFilterEmpty(collectionFilter)) {
      // default map filter to in-progress if visiting page for the first time
      this.locationsCollection.setFilter({ statuses: ['in_progress'] });
    }
    
    const pageState = PageStateDAO.findPageState(PageNames.MOBILE_MAPS);

    this.state = {
      appliedProjectsFilters: this.locationsCollection.getFilter(),
      selectedLocation: pageState ? pageState.selectedLocation : null,
      mapCenter: pageState ? pageState.mapCenter : null,
      mapZoom: pageState ? pageState.mapZoom : null,
    };

    this.statusesFilter = {
      title: 'Project Status',
      initiallyExpanded: true,
      options: [
        { value: -1, name: '--All Project Statuses--', type: 'statuses' },
        { value: 'not_started', name: 'Not Started', type: 'statuses' },
        { value: 'in_progress', name: 'In Progress', type: 'statuses' },
        { value: 'complete', name: 'Completed', type: 'statuses' },
      ],
    };
    this.filterOptions = [this.statusesFilter];

    // initialize map bounds to whole world to generate all clusters and then fit map to them
    this.mapBounds=[-180, -90, 180, 90];

    const currentRole = props.currentUser ? props.currentUser.role : null;
    this.analyticsLogger = new AnalyticsLogger({
      currentRole,
      isMobile: !!window.cordova,
    });

    this.handlePinClick = this.handlePinClick.bind(this);
    this.handleHidePopup = this.handleHidePopup.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleApplyFilters = this.handleApplyFilters.bind(this);
    this.handleSelectProject = this.handleSelectProject.bind(this);
    this.handleMapOnChange = this.handleMapOnChange.bind(this);
  }

  componentDidMount() {
    this.fetchLocations();

    dataModel.usersDAO.getProjectManagers(null).then(projectManagers => {
      projectManagers.unshift({ id: -1, all: true, first_name: '-- All Project Managers --', last_name: '' });
      const managersOptions = {
        title: 'Project Managers',
        initiallyExpanded: false,
        options: projectManagers.map(project_manager => { 
          return { 
            value: project_manager.id, 
            name: project_manager.first_name + ' ' + project_manager.last_name,
            type: 'project_managers',
          };
        }),
      };
      this.filterOptions = [...this.filterOptions, managersOptions];
      this.setState({ projectManagers });
    });
    
    this.projectTypesCollection = ProjectTypesCollection.build(null, null, localStorage.current_company_id);
    this.projectTypesCollection.fetchCurrent().then(projectTypes => {
      projectTypes.unshift({ id: -1, all: true, name: '-- All Project Types --' });

      const projectTypesOptions = {
        title: 'Project Types',
        initiallyExpanded: false,
        options: projectTypes.map(project_type => {
          return {
            value: project_type.id,
            name: project_type.name,
            type: 'project_types',
          };
        }),
      };

      this.filterOptions = [...this.filterOptions, projectTypesOptions];
      this.setState({ projectTypes });
    });
  }

  fetchLocations() {
    let { selectedLocation } = this.state;
    this.locationsCollection.fetchWithProjects().then(collectionData => {
      this.locationsLoaded = true;
      if (selectedLocation) {
        const updatedLocation = collectionData.locations.find(location => location.id == selectedLocation.id);
        // update selected location with latest data, or set to null if not found
        selectedLocation = updatedLocation;
        PageStateDAO.setPageStateEntry(PageNames.MOBILE_MAPS, 'selectedLocation', selectedLocation);
      }
      this.setState({ locations: collectionData.locations, selectedLocation });
    });
  }

  handleClear() {
    this.setState({ appliedProjectsFilters: {} });
    this.locationsCollection.reset();
    this.fetchLocations();
  }

  handleApplyFilters(filter) {
    if (filter.project_managers) {
      filter.project_managers = filter.project_managers.map(managerId => this.state.projectManagers.find(storedManagers => storedManagers.id == managerId));
    }
    if (filter.project_types) {
      filter.project_types = filter.project_types.map(typeId => this.state.projectTypes.find(storedTypes => storedTypes.id == typeId));
    }
    this.setState({ appliedProjectsFilters: filter });
    this.locationsCollection.setFilter(Object.assign({}, filter));
    this.fetchLocations();
    this.analyticsLogger.logMaps('MapFilter');
  }

  isFilterEmpty(filter) {
    return _.isEmpty(filter) || ((!filter.project_types || filter.project_types.length == 0) &&
      (!filter.project_managers || filter.project_managers.length == 0) && (!filter.statuses || filter.statuses.length == 0));
  }

  handlePinClick(location) {
    let mapCenter = null;
    if (location) {
      mapCenter = { lat: location.latitude, lng: location.longitude };
    }
    PageStateDAO.setPageStateEntry(PageNames.MOBILE_MAPS, 'selectedLocation', location);
    this.setState({ selectedLocation: location, mapCenter });
    this.analyticsLogger.logMaps('MapLocationClick');
  }

  handleSelectProject(project) {
    this.props.history.push('/mobile/projects/'+project.id);
    this.analyticsLogger.logMaps('MapProjectClick');
  }

  handleHidePopup() {
    PageStateDAO.setPageStateEntry(PageNames.MOBILE_MAPS, 'selectedLocation', null);
    this.setState({ selectedLocation: null });
  }

  renderLocations() {
    const { locations, mapZoom } = this.state;
    if (locations && locations.length) {
      const pinsForMap = [];
      const geoJSONPoints = [];
      for (let location of locations) {
        if (location.latitude && location.longitude) {
          geoJSONPoints.push(MapsHelpers.getGeoJSONPoint(location.latitude, location.longitude, { location }));
        }
      }

      const superCluster = MapsHelpers.buildSuperClusterInstance(geoJSONPoints);
      const features = superCluster.getClusters(this.mapBounds, mapZoom || 0);
      for (let feature of features) {
        const { lat, lng } = MapsHelpers.getLatLngFromGeoJSONFeature(feature);
        if (feature.properties && feature.properties.cluster) {
          // it's a cluster (multiple locations)
          const locationsInCluster = superCluster.getLeaves(feature.properties.cluster_id, Infinity);
          pinsForMap.push(
            <LocationMapCluster 
              lat={lat}
              lng={lng}
              locationsGeoJSON={locationsInCluster}
            />
          );
        }
        else {
          // it's a point (one location)
          const { location } = feature.geometry.properties;
          pinsForMap.push(
            <MobileLocationMapMarker
              key={location.id}
              lat={lat}
              lng={lng}
              location={location}
              onClick={this.handlePinClick}
            />
          );
        }
      }
      return pinsForMap;
    }
  }
  handleAppliedFilters(filters){ //dont delete
    
  }

  handleMapOnChange(marginBounds) {
    if (marginBounds.userInitiated) {
      if (marginBounds.lastOnChange.zoom != marginBounds.zoom) {
        this.analyticsLogger.logMaps('DashboardMapZoom');
      }
      if (marginBounds.lastOnChange.center.lat != marginBounds.center.lat
        || marginBounds.lastOnChange.center.lng != marginBounds.center.lng) {
        this.analyticsLogger.logMaps('DashboardMapPanned');
      }
      this.setState({
        mapCenter: marginBounds.center,
        mapZoom: marginBounds.zoom,
      });
      PageStateDAO.setPageState(PageNames.MOBILE_MAPS, {
        mapCenter: marginBounds.center,
        mapZoom: marginBounds.zoom,
        visited: true,
      });
    }
    if (this.state.locations) {
      // don't change bounds until locations loaded
      this.mapBounds = marginBounds.boundsFormatted;
      this.setState({
        mapCenter: marginBounds.center,
        mapZoom: marginBounds.zoom,
      });
    }
  }

  render() {
    const locationsLoaded = !!this.state.locations;
    const appliedFilters = { ...this.state.appliedProjectsFilters };
    if (appliedFilters.project_managers) {
      appliedFilters.project_managers = appliedFilters.project_managers.map(manager => manager.id);
    }
    if (appliedFilters.project_types) {
      appliedFilters.project_types = appliedFilters.project_types.map(type => type.id);
    }

    return (
      <div className="mobile-wrapper">
        <MobileHeader 
          headerTitle={'Maps'} 
          noBackButton={this.noBackButton}
          hasFilters={true}
          filterOptions={this.filterOptions}
          handleClear={this.handleClear}
          appliedFilters={appliedFilters}
          applyFilters={this.handleApplyFilters}
          handleAppliedFilters={this.handleAppliedFilters}
        />
        <div className="content-wrapper">
          {  
            <MapView 
              mapOptions={this.mapOptions} 
              handleOnChange={this.handleMapOnChange}
              center={this.state.mapCenter}
              zoom={this.state.mapZoom}
            >
              { locationsLoaded && this.renderLocations() }
            </MapView>
          }
          {this.state.selectedLocation &&
            <MapLocationPopup 
              id={this.state.selectedLocation.id} 
              key={'projects_' + this.state.selectedLocation.id} 
              location={this.state.selectedLocation}
              handleHidePopup={this.handleHidePopup}
              handleSelectProject={this.handleSelectProject}
              currentUser={this.props.currentUser} 
            />
          }
          {!locationsLoaded && <LoaderPopup text="Loading Locations" />}
        </div>
      </div>
    );
  }
}

MapsWrapper.contextTypes = {
  router: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
  return {
    currentUser: state.new_users.currentUser,
  };
};
export default withRouter(connect(mapStateToProps)(MapsWrapper));



// WEBPACK FOOTER //
// ./src/mobileWrappers/MapsWrapper/index.js